import { useState } from "react";
import { Reveal } from "@/components/Reveal";
import { TIERS, FOUNDING_LEFT, FOUNDING_TOTAL } from "@/lib/brand";
import { Check, Loader2, ShieldCheck } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const API = process.env.REACT_APP_BACKEND_URL;

export default function Pricing() {
  const [billing, setBilling] = useState("monthly"); // monthly | yearly
  const [modalTier, setModalTier] = useState(null); // tier object being subscribed to
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const openSubscribe = (tier) => {
    setModalTier(tier);
    setError("");
  };

  const subscribe = async () => {
    if (!email || !modalTier) return;
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API}/api/payments/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          lookup_key: modalTier.lookup[billing],
          email,
          origin_url: window.location.origin,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Could not start checkout. Please try again.");
      window.location.href = data.checkout_url;
    } catch (e) {
      setError(e.message);
      setLoading(false);
    }
  };

  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="relative grain bg-[#2B2B2B] text-white py-20 lg:py-28 overflow-hidden"
    >
      <div className="absolute inset-0 techgrid-dark opacity-60" />
      <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal className="text-center">
          <p className="overline text-[#2BBFB3]">App-only plans</p>
          <h2 className="mt-5 font-display font-extrabold tracking-tight text-4xl sm:text-5xl leading-[1.02]">
            Not ready for hardware? Start with the app.
          </h2>
          <p className="mt-5 max-w-xl mx-auto text-white/60">
            Prefer to use your own device for now? These app-only plans cover every seat in the car — from
            learners and supervisors to full driving schools. Instructors start free; founders lock their price for life.
          </p>
        </Reveal>

        {/* billing toggle */}
        <Reveal delay={0.05} className="mt-8 flex justify-center">
          <div className="inline-flex items-center rounded-full border border-white/15 bg-white/[0.04] p-1" data-testid="billing-toggle">
            {[
              { key: "monthly", label: "Monthly" },
              { key: "yearly", label: "Annual · 2 months free" },
            ].map((opt) => (
              <button
                key={opt.key}
                data-testid={`billing-${opt.key}`}
                onClick={() => setBilling(opt.key)}
                className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors ${
                  billing === opt.key ? "bg-[#2BBFB3] text-[#0f1211]" : "text-white/60 hover:text-white"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </Reveal>

        <div className="mt-10 flex gap-5 overflow-x-auto pb-4 snap-x snap-mandatory lg:grid lg:grid-cols-4 lg:gap-5 lg:overflow-visible">
          {TIERS.map((t, i) => {
            const featured = t.variant === "featured";
            const paid = Boolean(t.lookup);
            const price = paid ? t[billing].price : t.price;
            const cadence = paid ? t[billing].cadence : t.cadence;
            const alt = paid
              ? billing === "monthly"
                ? `or ${t.yearly.price}/year — ${t.yearly.note}`
                : `or ${t.monthly.price}/month`
              : null;
            return (
              <Reveal key={t.name} delay={i * 0.06} className="snap-start shrink-0 w-[82%] sm:w-[46%] lg:w-auto">
                <div
                  data-testid={`pricing-${featured ? "founder" : t.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className={`relative h-full rounded-2xl p-6 flex flex-col transition-transform duration-300 ${
                    featured
                      ? "bg-white text-[#2B2B2B] lg:-translate-y-4 ring-2 ring-[#F5A623] shadow-[0_0_50px_rgba(245,166,35,0.25)]"
                      : "bg-white/[0.04] border border-white/10 hover:border-white/25"
                  }`}
                >
                  {featured && (
                    <span
                      data-testid="founder-limited-badge"
                      className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-[#F5A623] px-4 py-1 text-[11px] font-bold uppercase tracking-wide text-[#2B2B2B]"
                    >
                      {`${FOUNDING_LEFT} of ${FOUNDING_TOTAL} spots left`}
                    </span>
                  )}

                  <div className={`text-xs font-semibold uppercase tracking-widest ${featured ? "text-[#B57608]" : "text-[#2BBFB3]"}`}>
                    {t.name}
                  </div>
                  <div className="mt-4 flex items-end gap-1">
                    <span className="font-display font-extrabold text-4xl leading-none" data-testid={`pricing-price-${i}`}>{price}</span>
                    {cadence && (
                      <span className={`text-sm mb-1 ${featured ? "text-[#2B2B2B]/50" : "text-white/50"}`}>{cadence}</span>
                    )}
                  </div>
                  {alt && (
                    <p className={`mt-1.5 text-xs font-semibold ${featured ? "text-[#B57608]" : "text-[#2BBFB3]"}`}>{alt}</p>
                  )}
                  <p className={`mt-2 text-sm ${featured ? "text-[#2B2B2B]/60" : "text-white/55"}`}>{t.audience}</p>

                  <ul className="mt-6 space-y-3 flex-1">
                    {t.features.map((f) => (
                      <li key={f} className="flex items-start gap-2.5 text-sm">
                        <Check
                          size={16}
                          className={`mt-0.5 shrink-0 ${featured ? "text-[#F5A623]" : "text-[#2BBFB3]"}`}
                        />
                        <span className={featured ? "text-[#2B2B2B]/80" : "text-white/75"}>{f}</span>
                      </li>
                    ))}
                  </ul>

                  {paid ? (
                    <button
                      onClick={() => openSubscribe(t)}
                      data-testid={`pricing-subscribe-${t.lookup.monthly.replace("_monthly", "")}`}
                      className={`mt-7 inline-flex items-center justify-center rounded-full px-5 py-3 text-sm font-semibold transition-colors ${
                        featured
                          ? "bg-[#F5A623] text-[#2B2B2B] hover:bg-[#e0940f]"
                          : t.variant === "pro"
                          ? "border border-[#F5A623]/60 text-[#F5A623] hover:bg-[#F5A623] hover:text-[#2B2B2B]"
                          : "bg-[#2BBFB3] text-[#0f1211] hover:bg-[#1FA097]"
                      }`}
                    >
                      {t.cta}
                    </button>
                  ) : (
                    <a
                      href="#download"
                      data-testid={`pricing-cta-${i}`}
                      className="mt-7 inline-flex items-center justify-center rounded-full px-5 py-3 text-sm font-semibold transition-colors bg-[#2BBFB3] text-[#0f1211] hover:bg-[#1FA097]"
                    >
                      {t.cta}
                    </a>
                  )}
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={0.1}>
          <p className="mt-8 flex items-center justify-center gap-2 text-sm text-white/45" data-testid="pricing-stripe-note">
            <ShieldCheck size={15} className="text-[#2BBFB3]" />
            Secure checkout via Stripe · cancel anytime · use the same email in the app and your plan unlocks automatically
          </p>
        </Reveal>
      </div>

      {/* subscribe modal */}
      <Dialog open={Boolean(modalTier)} onOpenChange={(open) => !open && !loading && setModalTier(null)}>
        <DialogContent data-testid="subscribe-modal" className="bg-white text-[#2B2B2B] sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              Subscribe to {modalTier?.name}
            </DialogTitle>
            <DialogDescription className="text-[#2B2B2B]/60">
              {modalTier && (
                <>
                  {modalTier[billing].price}{modalTier[billing].cadence} — billed {billing === "monthly" ? "monthly" : "annually"}.
                  Use the <span className="font-semibold text-[#2B2B2B]">same email</span> as your DriveAssure app
                  account so your plan is recognised when you log in.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="mt-2 space-y-4">
            <input
              type="email"
              required
              data-testid="subscribe-email-input"
              placeholder="you@yourdrivingschool.com.au"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && subscribe()}
              className="w-full rounded-xl border border-[#E5E5E5] bg-white px-4 py-3 text-sm outline-none focus:border-[#2BBFB3] focus:ring-2 focus:ring-[#2BBFB3]/20"
            />
            {error && (
              <p data-testid="subscribe-error" className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
                {error}
              </p>
            )}
            <button
              onClick={subscribe}
              disabled={loading || !email.includes("@")}
              data-testid="subscribe-submit-btn"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#2B2B2B] px-6 py-3.5 text-sm font-semibold text-white hover:bg-black transition-colors disabled:opacity-50"
            >
              {loading && <Loader2 size={16} className="animate-spin" />}
              {loading ? "Opening secure checkout…" : "Continue to secure checkout"}
            </button>
            <p className="text-center text-xs text-[#2B2B2B]/45">
              Payments handled by Stripe. We never see your card details.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
